import React from 'react';
import PropTypes from 'prop-types';
import { Autocomplete, TextField } from '@totalsoft/rocket-ui';
import { Grid } from '@mui/material';
import { get, over, set } from '@totalsoft/change-tracking-react';
import { useFieldDecimalPlacesConfig } from 'hooks';
import LabeledCheckbox from 'components/common/inputs/LabeledCheckbox';
import { onSimpleSelectChange, valueOrDefault } from 'utils/functions';
import { EventCollapseCard } from '../../Styles';
import { useReactiveVar } from '@apollo/client';
import { selectedSchemaDetailIdVar } from '../../variables';
import IconButton from 'components/common/buttons/IconButton';
import { useTranslation } from 'react-i18next';
import { getErrors, isValid } from '@totalsoft/pure-validations-react';

const LeasingIncomeSchemaEventComponent = ({
  lens,
  entityEvents,
  incomeTypes,
  currencies,
  leasingExpenseMeasuringUnits,
  leasingExpenseNegotiationMoments,
  onDelete,
  validation,
  disabled
}) => {
  const { t } = useTranslation();

  const selectedSchemaDetailId = useReactiveVar(selectedSchemaDetailIdVar);
  const { cvsChPercent, cvsChAmount } = useFieldDecimalPlacesConfig();

  const entityEvent = { id: lens.eventId |> get, name: lens.eventName |> get };

  const incomeType = {
    id: lens.incomeTypeId |> get,
    code: lens.incomeTypeCode |> get,
    name: lens.incomeTypeName |> get,
    currencyName: lens.incomeTypeCurrencyName |> get,
    useFormula: lens.incomeTypeUseFormula |> get
  };

  const currency = { id: lens.currencyId |> get, name: lens.currencyName |> get };

  const measuringUnit = { id: lens.measuringUnitId |> get, name: lens.measuringUnitName |> get };

  const negotiationMoment = { id: lens.negotiationMomentId |> get, name: lens.negotiationMomentName |> get };

  const handleIncomeTypeChange = value => {
    over(lens, prev => {
      const next = {
        ...prev,
        incomeTypeId: value.id,
        incomeTypeName: value.name,
        incomeTypeCode: value.code,
        incomeTypeCurrencyName: value.currencyName,
        incomeTypeUseFormula: value.useFormula
      };
      if (value.currencyId) {
        next.currencyId = value.currencyId;
        next.currencyCode = value.currencyCode;
        next.currencyName = value.currencyName;
      }
      return next;
    });
  };

  const getTitle = () => {
    const event = lens.eventId |> get ? (lens.eventName |> get) || '' : 'No event';
    const incomeType = lens.incomeTypeId |> get ? (lens.incomeTypeName |> get) || '' : 'No income type';
    const currency = lens.currencyId |> get ? `(${(lens.currencyCode |> get) || ''})` : '';
    return `${event} - ${incomeType} ${currency}`;
  };

  const selected = (lens.id |> get) === selectedSchemaDetailId;

  return (
    <EventCollapseCard
      title={getTitle()}
      onClick={() => selectedSchemaDetailIdVar(lens.id |> get)}
      selected={selected}
      actions={<IconButton type='delete' color='error' onClick={() => onDelete(lens.id |> get)} disabled={disabled} />}
    >
      <Grid container spacing={2}>
        <Grid item xs={12} sm={4}>
          <Autocomplete
            label={t('LeasingIncomeSchema.Event')}
            value={entityEvent}
            onChange={onSimpleSelectChange(lens, 'event')}
            options={entityEvents}
            disabled={disabled}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <Autocomplete
            label={t('LeasingIncomeSchema.IncomeType')}
            required
            value={incomeType}
            onChange={handleIncomeTypeChange}
            options={incomeTypes}
            error={!isValid(validation.incomeTypeId)}
            helperText={getErrors(validation.incomeTypeId)}
            disabled={disabled}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <LabeledCheckbox
            label={t('LeasingIncomeSchema.CalculateUsingFormula')}
            disabled
            checked={(lens.incomeTypeUseFormula |> get) || false}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <Autocomplete
            label={t('LeasingIncomeSchema.Currency')}
            required
            value={currency}
            onChange={onSimpleSelectChange(lens, 'currency')}
            options={currencies}
            error={!isValid(validation.currencyId)}
            helperText={getErrors(validation.currencyId)}
            disabled={disabled}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <TextField
            label={t('LeasingIncomeSchema.Quota')}
            fullWidth
            isNumeric
            decimalScale={cvsChPercent}
            value={lens.currencyPercent |> get |> valueOrDefault('')}
            onChange={lens.currencyPercent |> set}
            error={!isValid(validation.currencyPercent)}
            helperText={getErrors(validation.currencyPercent)}
            disabled={disabled}
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <TextField
            label={t('LeasingIncomeSchema.Value')}
            fullWidth
            isNumeric
            decimalScale={cvsChAmount}
            value={lens.currencyValue |> get |> valueOrDefault('')}
            onChange={lens.currencyValue |> set}
            error={!isValid(validation.currencyValue)}
            helperText={getErrors(validation.currencyValue)}
            disabled={disabled}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            label={t('LeasingIncomeSchema.MinimumValue')}
            fullWidth
            isNumeric
            decimalScale={cvsChAmount}
            value={lens.minValue |> get |> valueOrDefault('')}
            onChange={lens.minValue |> set}
            error={!isValid(validation.minValue)}
            helperText={getErrors(validation.minValue)}
            disabled={disabled}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            label={t('LeasingIncomeSchema.MaximumValue')}
            fullWidth
            isNumeric
            decimalScale={cvsChAmount}
            value={lens.maxValue |> get |> valueOrDefault('')}
            onChange={lens.maxValue |> set}
            error={!isValid(validation.maxValue)}
            helperText={getErrors(validation.maxValue)}
            disabled={disabled}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <Autocomplete
            label={t('LeasingIncomeSchema.MeasuringUnit')}
            value={measuringUnit}
            onChange={onSimpleSelectChange(lens, 'measuringUnit')}
            options={leasingExpenseMeasuringUnits}
            disabled={disabled}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <Autocomplete
            label={t('LeasingIncomeSchema.NegotiationMoment')}
            value={negotiationMoment}
            onChange={onSimpleSelectChange(lens, 'negotiationMoment')}
            options={leasingExpenseNegotiationMoments}
            disabled={disabled}
          />
        </Grid>
      </Grid>
    </EventCollapseCard>
  );
};

LeasingIncomeSchemaEventComponent.propTypes = {
  lens: PropTypes.object,
  entityEvents: PropTypes.array,
  incomeTypes: PropTypes.array,
  currencies: PropTypes.array,
  leasingExpenseMeasuringUnits: PropTypes.array,
  leasingExpenseNegotiationMoments: PropTypes.array,
  onDelete: PropTypes.func,
  validation: PropTypes.object,
  disabled: PropTypes.bool
};

export default LeasingIncomeSchemaEventComponent;
